import {
  Alert,
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { formatMinor } from '@/api/commercial';
import { fetchQuotations } from '@/api/quotations';
import type { QuotationStatus } from '@/types/generated';

const STATUS_COLOUR: Record<QuotationStatus, 'default' | 'info' | 'success' | 'error' | 'warning'> =
  {
    draft: 'default',
    issued: 'info',
    accepted: 'success',
    rejected: 'error',
    expired: 'warning',
    superseded: 'default',
    withdrawn: 'default',
  };

const STATUSES: { value: QuotationStatus | ''; label: string }[] = [
  { value: '', label: 'Any status' },
  { value: 'draft', label: 'Draft' },
  { value: 'issued', label: 'Issued' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
];

/**
 * The quote list.
 *
 * ONE ROW PER NEGOTIATION, not per revision. Showing v1, v2 and v3 of the same quote as
 * three rows reads as three opportunities, and every pipeline figure built on that view
 * would triple-count. The toggle exists for the case where someone genuinely wants the
 * revision history.
 */
export function QuotationsPage() {
  const [status, setStatus] = useState<QuotationStatus | ''>('');
  const [allRevisions, setAllRevisions] = useState(false);

  const query = useInfiniteQuery({
    queryKey: ['quotations', { status, allRevisions }],
    queryFn: ({ pageParam }) =>
      fetchQuotations({ status: status || undefined, allRevisions, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
  });

  const quotations = useMemo(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  return (
    <Stack spacing={2}>
      <Typography variant="h5">Quotations</Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(event) => setStatus(event.target.value as QuotationStatus | '')}
          sx={{ minWidth: 170 }}
        >
          {STATUSES.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={allRevisions}
              onChange={(event) => setAllRevisions(event.target.checked)}
            />
          }
          label="Show every revision"
        />
      </Stack>

      {query.isError && <Alert severity="error">Could not load quotations.</Alert>}

      <Card variant="outlined">
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Reference</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Calculated</TableCell>
                <TableCell align="right">Quoted</TableCell>
                <TableCell align="right">Δ</TableCell>
                <TableCell align="right">Margin</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isPending && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={22} />
                  </TableCell>
                </TableRow>
              )}

              {!query.isPending && quotations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No quotations yet. Price a submitted request to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {quotations.map((quotation) => (
                <TableRow key={quotation.id} hover>
                  <TableCell>
                    <Box
                      component={RouterLink}
                      to={`/quotations/${quotation.id}`}
                      sx={{ color: 'primary.main', textDecoration: 'none' }}
                    >
                      {quotation.label}
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {quotation.rfq_reference}
                    </Typography>
                  </TableCell>
                  <TableCell>{quotation.customer_name}</TableCell>
                  <TableCell align="right">
                    {formatMinor(quotation.calculated_total_minor, quotation.currency)}
                  </TableCell>
                  <TableCell align="right">
                    {formatMinor(quotation.final_price_minor, quotation.currency)}
                  </TableCell>
                  <TableCell align="right">
                    {quotation.override_delta_basis_points === null ? (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    ) : (
                      <Tooltip title={quotation.override_reason ?? 'Overridden'}>
                        <Typography
                          variant="body2"
                          color={
                            quotation.override_delta_basis_points < 0 ? 'error.main' : 'success.main'
                          }
                        >
                          {signed(quotation.override_delta_basis_points)}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {quotation.margin_basis_points === null ? (
                      // Blank, not 0% — the difference matters to whoever reads this column.
                      <Tooltip title="No estimated cost recorded, so margin is unknown rather than zero.">
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      </Tooltip>
                    ) : (
                      `${(quotation.margin_basis_points / 100).toFixed(1)}%`
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={quotation.status}
                      color={STATUS_COLOUR[quotation.status]}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Card>
    </Stack>
  );
}

function signed(basisPoints: number): string {
  const percent = (basisPoints / 100).toFixed(1);

  return basisPoints > 0 ? `+${percent}%` : `${percent}%`;
}
